'use client';
// HoldingsRow — single-row JSX extracted from HoldingsList.tsx to keep that
// parent component under the 500-line god-file threshold. ~165 lines of
// per-row cell rendering with many closures over local state, passed as
// props now instead.

import {
  compTier,
  INDEX_ETFS,
  TRIM_THRESHOLD,
} from './HoldingsList.helpers';
import { firstWord, type Holding, type TargetPriceRow, type HedgeSignal } from './HoldingsList.types';
import type { ScoreRow } from '@/lib/hooks/use-holdings-data';
import { SignalDot } from '@/components/SignalDot';
import { SignalCell, buildCellTitles, type SymbolSignals, type SignalCellType } from './HoldingSignalCells';
import { TargetPriceModal } from '@/components/stock-detail/TargetPriceModal';
import { ThreeYearModal } from '@/components/stock-detail/ThreeYearModal';
import { useState } from 'react';

interface PortfolioShape {
  shares?: Record<string, number>;
  entryPrices?: Record<string, number>;
  bookNav?: number;
}

interface VerdictRow { verdict: 'BUY' | 'ACCEPTABLE' | 'WAIT'; hedge: boolean; reason: string }

interface Props {
  h: Holding;
  gridColsCls: string;
  showLots: boolean;
  editing: boolean;
  saving: boolean;
  filterMode: 'all' | 'sell';
  draft: Record<string, string>;
  setDraft: (updater: (d: Record<string, string>) => Record<string, string>) => void;
  scores: Map<string, ScoreRow> | null;
  tpBySymbol: Map<string, TargetPriceRow>;
  alphaDaysBySymbol: Map<string, number>;
  hedgeBySymbol: Record<string, HedgeSignal>;
  signalsBySymbol: Record<string, SymbolSignals>;
  verdictBySymbol: Record<string, VerdictRow>;
  fcvBySymbol: Record<string, { dC: number; dQ: number }>;
  portfolio: PortfolioShape | null;
  onSelectSymbol?: (symbol: string) => void;
  /** Opens the small per-signal popover instead of the full drawer. */
  onSignalClick?: (symbol: string, signalType: SignalCellType) => void;
  handleRemoveRow: (symbol: string) => void;
}

export function HoldingsRow({
  h, gridColsCls, showLots, editing, saving, filterMode,
  draft, setDraft, scores, tpBySymbol, alphaDaysBySymbol, hedgeBySymbol,
  signalsBySymbol, verdictBySymbol, fcvBySymbol,
  portfolio, onSelectSymbol, onSignalClick, handleRemoveRow,
}: Props) {
  const [showFvModal, setShowFvModal] = useState(false);
  const [show3YrModal, setShow3YrModal] = useState(false);
  const tier = compTier(h.composite);
  const isSellish = !INDEX_ETFS.has(h.symbol) && h.composite != null && h.composite < TRIM_THRESHOLD;
  const renderPnl = (v: number | null) => {
    if (v == null) return <span className="text-[#475569]">—</span>;
    const color = v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-[#94a3b8]';
    const sign = v >= 0 ? '+' : '';
    return <span className={`tabular-nums ${color}`}>{`${sign}${(v * 100).toFixed(1)}%`}</span>;
  };
  const alphaDays = alphaDaysBySymbol.get(h.symbol);
  const hedge = hedgeBySymbol[h.symbol];
  const normalizedSh = portfolio?.shares?.[h.symbol] ?? null;
  const realShares = normalizedSh != null ? normalizedSh * (portfolio?.bookNav ?? 1_000_000) : null;
  const entryPx = portfolio?.entryPrices?.[h.symbol] ?? null;

  // Green-tint rows currently in the Alpha Zone basket (αDays > 0). Visually
  // marks book holdings that the live model has surfaced as a top-10 idea —
  // the "our portfolio overlaps with our research" story. Red-tint sellish
  // rows takes precedence (a sellish row in alpha shouldn't exist by design;
  // if it does, the red flag wins).
  const inAlphaBasket = alphaDays != null && alphaDays > 0;
  const rowTint = isSellish
    ? 'bg-red-950/15'
    : inAlphaBasket
      ? 'bg-emerald-950/30'
      : '';
  const v = verdictBySymbol[h.symbol];
  const stColor = v?.hedge
    ? 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.85)] animate-pulse'
    : v?.verdict === 'WAIT'
      ? 'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.7)]'
      : v?.verdict === 'BUY' || v?.verdict === 'ACCEPTABLE'
        ? 'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]'
        : 'bg-zinc-600/40';
  const stTitle = v?.hedge
    ? `HEDGE: bear-skew signal active. Click → opens /hedge-lab.`
    : v
      ? `${v.verdict} — ${v.reason}. Click → opens two-pager.`
      : 'No verdict data yet.';
  return (
    <>
    <div
      className={`grid ${gridColsCls} gap-1 px-2 h-[25px] text-[9px] leading-tight items-center border-b border-[#1e293b]/60 hover:bg-[#1e293b]/30 transition-colors overflow-hidden ${rowTint}`}
    >
      <div className="col-span-1 flex items-center justify-center self-center">
        {v?.hedge ? (
          <a
            href={`/hedge-lab?symbol=${encodeURIComponent(h.symbol)}`}
            onClick={(e) => e.stopPropagation()}
            title={stTitle}
            className={`inline-block w-3 h-3 rounded-full ${stColor} cursor-pointer`}
          />
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSelectSymbol?.(h.symbol); }}
            title={stTitle}
            className={`inline-block w-3 h-3 rounded-full ${stColor} cursor-pointer`}
            aria-label={`Open ${h.symbol} two-pager`}
          />
        )}
      </div>
      <div
        className={`col-span-1 font-mono font-bold ${tier.color} whitespace-nowrap overflow-hidden flex items-center gap-1 ${onSelectSymbol ? 'cursor-pointer' : ''}`}
        onClick={() => onSelectSymbol?.(h.symbol)}
        title={onSelectSymbol ? `Open ${h.symbol} details` : undefined}
      >
        {editing && filterMode !== 'sell' && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleRemoveRow(h.symbol); }}
            disabled={saving}
            title={`Remove ${h.symbol} (saves immediately)`}
            className="text-red-400/70 hover:text-red-300 leading-none px-0.5 text-[11px] font-bold disabled:opacity-30"
          >
            ×
          </button>
        )}
        <span>{h.symbol}</span>
      </div>
      <div className="col-span-1 flex items-center justify-center">
        <SignalDot symbol={h.symbol} size={6} />
      </div>
      {(() => {
        const sc = scores?.get(h.symbol);
        const fw = firstWord(sc?.name ?? null);
        const mc = sc?.marketCap;
        return (
          <>
            <div className="col-span-1 text-[9px] text-[#cbd5e1] truncate" title={sc?.name ?? ''}>{fw || '—'}</div>
            <div className="col-span-1 text-right tabular-nums text-[#94a3b8]">
              {mc != null && mc > 0 ? `$${Math.round(mc / 1e9)}B` : '—'}
            </div>
          </>
        );
      })()}
      {editing ? (
        <div className="col-span-1 text-right">
          <input
            type="number"
            step="0.1"
            min="0"
            value={draft[h.symbol] ?? ''}
            onChange={(e) => setDraft(d => ({ ...d, [h.symbol]: e.target.value }))}
            className="w-full bg-[#0a0e14] border border-[#334155] focus:border-amber-500/60 focus:outline-none rounded px-1 py-0 text-right tabular-nums text-[9px] text-amber-200"
          />
        </div>
      ) : (
        <div className="col-span-1 text-right tabular-nums text-[#cbd5e1]">{(h.weight * 100).toFixed(1)}%</div>
      )}
      {showLots && (
        <>
          <div
            className="col-span-1 text-right tabular-nums text-[#cbd5e1]"
            title={realShares != null && entryPx != null && h.price != null
              ? `${realShares.toFixed(0)} shares · cost basis $${entryPx.toFixed(2)} · current $${h.price.toFixed(2)} · unrealised ${((h.price - entryPx) * realShares >= 0 ? '+' : '')}$${((h.price - entryPx) * realShares).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : 'real share count'}
          >
            {realShares != null ? realShares.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
          </div>
          <div
            className="col-span-1 text-right tabular-nums text-[#cbd5e1]"
            title={entryPx != null && h.price != null
              ? `Entry $${entryPx.toFixed(2)} vs current $${h.price.toFixed(2)} = ${(((h.price / entryPx) - 1) * 100 >= 0 ? '+' : '')}${(((h.price / entryPx) - 1) * 100).toFixed(1)}%`
              : 'Cost basis (price paid)'}
          >
            {entryPx != null ? `$${entryPx.toFixed(2)}` : '—'}
          </div>
        </>
      )}
      <div className="col-span-1 text-right tabular-nums text-[#cbd5e1]" title={h.price != null ? 'Live price (last refresh)' : 'No price'}>
        {h.price != null ? `$${h.price.toFixed(2)}` : '—'}
      </div>
      <div className="col-span-1 text-right">{renderPnl(h.pnl.dayPct)}</div>
      <div className="col-span-1 text-right">{renderPnl(h.pnl.mtdPct)}</div>
      <div className="col-span-1 text-right">{renderPnl(h.pnl.ytdPct)}</div>
      <div className="col-span-1 text-right">{renderPnl(h.pnl.off52wHigh)}</div>
      {(() => {
        const tp = tpBySymbol.get(h.symbol);
        const fv = (tp as Record<string, number | null> | undefined)?.calibratedFV ?? tp?.consensusFairValue ?? tp?.todayFairValue ?? null;
        if (fv == null || h.price == null || h.price <= 0) {
          return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
        }
        const src = (tp as Record<string, number | null> | undefined)?.calibratedFV != null ? 'regression-calibrated' : 'consensus';
        return (
          <div
            className="col-span-1 text-right tabular-nums text-blue-300 cursor-pointer hover:text-blue-200 transition-colors"
            title={`FV $${fv.toFixed(2)} (${src}). Click to see full derivation.`}
            onClick={(e) => { e.stopPropagation(); setShowFvModal(true); }}
          >
            ${Math.round(fv)}
          </div>
        );
      })()}
      {(() => {
        const tp = tpBySymbol.get(h.symbol);
        const fv = (tp as Record<string, number | null> | undefined)?.calibratedFV ?? tp?.consensusFairValue ?? tp?.todayFairValue ?? null;
        if (fv == null || h.price == null || h.price <= 0) {
          return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
        }
        const pct = (fv / h.price - 1) * 100;
        const color = pct >= 20 ? 'text-emerald-400' : pct >= 0 ? 'text-blue-300' : 'text-red-400';
        return (
          <div className={`col-span-1 text-right tabular-nums ${color}`} title={`1Y implied return to FV: ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}>
            {pct >= 0 ? '+' : ''}{pct.toFixed(0)}%
          </div>
        );
      })()}
      {(() => {
        const tp = tpBySymbol.get(h.symbol);
        const target = (tp as Record<string, number | null> | undefined)?.calibrated3Y ?? tp?.threeYearTarget ?? null;
        if (target == null) {
          return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
        }
        return (
          <div
            className="col-span-1 text-right tabular-nums text-blue-300 cursor-pointer hover:text-blue-200 transition-colors"
            title={`3Y target = FY-3 EPS × calibrated multiple = $${target.toFixed(2)}. Click to see derivation.`}
            onClick={(e) => { e.stopPropagation(); setShow3YrModal(true); }}
          >
            ${Math.round(target)}
          </div>
        );
      })()}
      {(() => {
        const tp = tpBySymbol.get(h.symbol);
        const target = (tp as Record<string, number | null> | undefined)?.calibrated3Y ?? tp?.threeYearTarget ?? null;
        if (target == null || h.price == null || h.price <= 0) {
          return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
        }
        const pct = (target / h.price - 1) * 100;
        const color = pct >= 50 ? 'text-emerald-400' : pct >= 0 ? 'text-amber-300' : 'text-red-400';
        return (
          <div className={`col-span-1 text-right tabular-nums ${color}`} title={`3Y target $${target.toFixed(2)} · asymmetry ${tp?.asymmetry?.toFixed(1) ?? "—"}:1`}>
            {pct >= 0 ? '+' : ''}{pct.toFixed(0)}%
          </div>
        );
      })()}
      {(() => {
        const tp = tpBySymbol.get(h.symbol);
        const asym = tp?.asymmetry ?? null;
        if (asym == null) return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
        const color = asym >= 4 ? 'text-emerald-400' : asym >= 2 ? 'text-amber-300' : 'text-red-400';
        const upPct = tp?.upside != null ? `${tp.upside >= 0 ? '+' : ''}${(tp.upside * 100).toFixed(0)}%` : '—';
        const downPct = tp?.downside != null ? `${(tp.downside * 100).toFixed(0)}%` : '—';
        return (
          <div className={`col-span-1 text-right tabular-nums ${color}`} title={`Up ${upPct} / Down ${downPct}`}>
            {asym.toFixed(1)}:1
          </div>
        );
      })()}
      <div className={`col-span-1 text-right tabular-nums font-semibold ${tier.color}`}>
        {h.composite == null ? '—' : h.composite.toFixed(2)}
      </div>
      {(() => {
        const fcv = fcvBySymbol[h.symbol];
        if (!fcv) return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
        const { dC, dQ } = fcv;
        const color = dC > 0.01 ? 'text-emerald-400' : dC < -0.01 ? 'text-red-400' : 'text-[#94a3b8]';
        const sign = dC >= 0 ? '+' : '';
        return (
          <div
            className={`col-span-1 text-right tabular-nums font-semibold ${color}`}
            title={`ΔC = ${sign}${dC.toFixed(3)} when next Q reports at consensus. ΔQ = ${dQ >= 0 ? '+' : ''}${dQ.toFixed(3)}. Negative on strong earners = blowout base compresses forward CAGR.`}
          >
            {sign}{dC.toFixed(2)}
          </div>
        );
      })()}
      {(() => {
        const s = signalsBySymbol[h.symbol];
        const titles = buildCellTitles(s);
        // Signal cells now open the small per-signal popover instead of the
        // full drawer (Steve directive 2026-05-22). Falls back to the drawer
        // when no popover handler is wired.
        const click = (type: SignalCellType) => {
          if (onSignalClick) onSignalClick(h.symbol, type);
          else onSelectSymbol?.(h.symbol);
        };
        return (
          <>
            <SignalCell signal={s?.cong   ?? 'none'} title={titles.cong}    onClick={() => click('cong')} />
            <SignalCell signal={s?.insider ?? 'none'} title={titles.insider} onClick={() => click('insider')} />
            <SignalCell signal={s?.call   ?? 'none'} title={titles.call}    onClick={() => click('call')} />
            <SignalCell signal={s?.earn   ?? 'none'} title={titles.earn}    onClick={() => click('earn')} />
          </>
        );
      })()}
      <div className="col-span-1 text-center self-center">
        {hedge ? (
          <a
            href={`/hedge-lab?symbol=${encodeURIComponent(h.symbol)}`}
            title={`HEDGE signal: asymZ ${hedge.asymZ.toFixed(2)} (bear-leaning) + 12m return ${(hedge.mom12 * 100).toFixed(0)}% (extended). Backtest: dampening these lifts Sharpe +0.05 / worst-year +2.1pp at -2.6pp CAGR cost. Click → /hedge-lab.`}
            className="inline-block px-1.5 py-[1px] rounded text-[7px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 hover:text-amber-200 transition-colors leading-none animate-pulse"
          >
            🛡
          </a>
        ) : (
          <span className="text-[#475569] text-[9px]">—</span>
        )}
      </div>
      <div className={`col-span-1 text-right text-[7px] uppercase tracking-wide self-center ${tier.color}`}>
        {tier.label}
      </div>
      <div className="col-span-1 text-right tabular-nums" title={alphaDays != null ? `In Alpha Zone for ${alphaDays} day(s) (since enter date in current 60-day window)` : 'Not currently in Alpha Zone'}>
        {alphaDays != null ? <span className="text-emerald-400 font-semibold">{alphaDays}d</span> : <span className="text-[#475569]">—</span>}
      </div>
    </div>
    {show3YrModal && (() => {
      const tp = tpBySymbol.get(h.symbol);
      if (!tp) return null;
      return (
        <ThreeYearModal
          d={{
            symbol: h.symbol,
            currentPrice: tp.currentPrice ?? h.price ?? null,
            threeYearTarget: (tp as unknown as Record<string, number | null>).calibrated3Y ?? tp.threeYearTarget ?? null,
            fy1Eps: tp.fy1Eps ?? null,
            fy3Eps: (tp as unknown as Record<string, number | null>).fy3Eps ?? null,
            targetMultiple: (tp as unknown as Record<string, unknown>).targetMultiple as { peerMedian?: number | null; ownMedian?: number | null; nearTerm?: number | null } | null ?? null,
            growth: (tp as unknown as Record<string, unknown>).growth as { gLtg?: number | null; g3y?: number | null; g5y?: number | null; median?: number | null; final?: number | null; qualityFade?: number | null } | null ?? null,
            calibrated3Y: (tp as unknown as Record<string, number | null>).calibrated3Y ?? null,
            dcfFairValue: tp.dcfFairValue ?? null,
          }}
          onClose={() => setShow3YrModal(false)}
        />
      );
    })()}
    {showFvModal && (() => {
      const tp = tpBySymbol.get(h.symbol);
      if (!tp) return null;
      return (
        <TargetPriceModal
          d={{
            symbol: h.symbol,
            currentPrice: tp.currentPrice,
            peerGroup: tp.peerGroup ?? 'standard',
            todayFairValue: tp.todayFairValue ?? null,
            fy1Eps: tp.fy1Eps ?? null,
            targetMultiple: tp.targetMultiple ?? null,
            evEbitdaMultiple: tp.evEbitdaMultiple ?? null,
            evEbitdaFairValue: tp.evEbitdaFairValue ?? null,
            fwdEbitdaRaw: tp.fwdEbitdaRaw ?? null,
            fwdNetDebtRaw: tp.fwdNetDebtRaw ?? null,
            marketCap: tp.marketCap ?? null,
            dcfFairValue: tp.dcfFairValue ?? null,
            dcfBreakdown: tp.dcfBreakdown ?? null,
            dcfDiscountRate: tp.dcfDiscountRate ?? null,
            costOfEquityBreakdown: tp.costOfEquityBreakdown ?? null,
            ownerEarningsPerShare: tp.ownerEarningsPerShare ?? null,
            pfcfMultiple: tp.pfcfMultiple ?? null,
            fcfPerShare: tp.fcfPerShare ?? null,
            fcfRaw: tp.fcfRaw ?? null,
            pfcfFairValue: tp.pfcfFairValue ?? null,
            consensusFairValue: tp.consensusFairValue ?? null,
            methodCoverage: tp.methodCoverage ?? null,
          }}
          onClose={() => setShowFvModal(false)}
        />
      );
    })()}
    </>
  );
}
